import HistoryIcon from '@mui/icons-material/History';
import InProgressIcon from '@mui/icons-material/Today';
import { Box, styled } from '@mui/material';
import { useMemo } from 'react';
import {
  FunctionField,
  NumberField,
  RecordContextProvider,
  SelectArrayInput,
  TopToolbar,
} from 'react-admin';
import { match } from 'ts-pattern';

import {
  GetB2bOperationErOneResponse,
  GetB2bOperationsErQuery,
  GetB2bOperationsErResponse,
  getB2bOperationsErSortColumns,
} from '@Common/types/bo/b2b/operations_er';

import {
  DatagridTyped,
  FilterButton,
  List,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import {
  b2bOperationErNatureChoices,
  b2bOperationErTypeChoices,
} from '@sh4res/bo-common/components/B2bOperationsEr/choices';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import {
  CurrencyField,
  IdField,
  ProductsField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort, Resources } from '@sh4res/bo-common/utils';
import { createFilters, FilterList } from '@sh4res/bo-common/utils/create_filters';
import { todayDateString } from '@sh4res/bo-common/utils/date';

type OperationErTab = 'in_progress' | 'history';
const testId = 'BoExternalB2bOperationsErList';

const b2bOperationsErFilters = createFilters<GetB2bOperationsErQuery>({
  id: <TextArrayInput asNumber source="" testId={`${testId}_IdInput`} />,
  operation_nature: (
    <SelectArrayInput
      choices={b2bOperationErNatureChoices}
      source=""
      data-testid={`${testId}_OperationNatureInput`}
    />
  ),
  operation_type: (
    <SelectArrayInput
      choices={b2bOperationErTypeChoices}
      source=""
      data-testid={`${testId}_OperationTypeInput`}
    />
  ),
  from_reference_period_start_date: (
    <DateInput source="" data-testid={`${testId}_FromReferencePeriodInput`} />
  ),
  to_reference_period_end_date: (
    <DateInput source="" data-testid={`${testId}_ToReferencePeriodInput`} />
  ),
} as FilterList<GetB2bOperationsErQuery>);

const defaultSort: RaSort<GetB2bOperationsErQuery> = { field: 'id', order: 'DESC' };

const b2bOperationsErListColumns = (
  resource: Resources,
): ColumnList<GetB2bOperationsErResponse[number]> => ({
  id: <IdField reference={resource} data-testid={`${testId}_IdField`} />,
  operation_nature: (
    <SelectChipField
      choices={b2bOperationErNatureChoices}
      data-testid={`${testId}_OperationNatureField`}
    />
  ),
  operation_type: (
    <SelectChipField
      choices={b2bOperationErTypeChoices}
      data-testid={`${testId}_OperationTypeField`}
    />
  ),
  amount_gross: <CurrencyField currency="EUR" testId={`${testId}_AmountGrossField`} />,
  amount_net: <CurrencyField currency="EUR" testId={`${testId}_AmountNetField`} />,
  products: <ProductsField data-testid={`${testId}_ProductsField`} />,
  employee_count: <NumberField emptyText="-" data-testid={`${testId}_EmployeeCountField`} />,
  processing_date: <UTCDateField showTime={false} data-testid={`${testId}_ProcessingDateField`} />,
  reference_period_start_date: (
    <FunctionField
      data-testid={`${testId}_ReferencePeriodField`}
      render={(record: GetB2bOperationErOneResponse) => (
        <>
          <UTCDateField
            record={record}
            source="reference_period_start_date"
            data-testid={`${testId}_ReferencePeriodStartDateField`}
          />
          -
          <UTCDateField
            record={record}
            source="reference_period_end_date"
            data-testid={`${testId}_ReferencePeriodEndDateField`}
          />
        </>
      )}
    />
  ),
  reference_period_end_date: (
    <UTCDateField showTime={false} data-testid={`${testId}_ReferencePeriodEndField`} />
  ),
});

const defaultColumns: (keyof GetB2bOperationErOneResponse)[] = [
  'id',
  'products',
  'operation_nature',
  'operation_type',
  'reference_period_start_date',
  'employee_count',
  'amount_gross',
];

const B2bOperationsErActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

export const B2bOperationsErList = () => (
  // Dummy record, because react-admin tabs requires a record to work
  <RecordContextProvider value={{ id: 1 }}>
    <VerticalTabsLayout>
      <VerticalTab
        icon={<InProgressIcon />}
        label="resources.bo_common.b2b.operations_er.tabs.in_progress"
        data-testid={`${testId}_InProgressTab`}
        path="in_progress">
        <ListView tab={'in_progress'} />
      </VerticalTab>
      <VerticalTab
        icon={<HistoryIcon />}
        data-testid={`${testId}_HistoryTab`}
        label="resources.bo_common.b2b.operations_er.tabs.history"
        path="history">
        <ListView tab={'history'} />
      </VerticalTab>
    </VerticalTabsLayout>
  </RecordContextProvider>
);

const ListView = ({ tab }: { tab: OperationErTab }) => {
  const resourceBasePath = useResourceBasePath();
  const resource: Resources = `${resourceBasePath}operations_er`;

  const displayedColumns = useSelectedColumns<GetB2bOperationErOneResponse>({
    preferences: 'b2b_operations_er.list.columns',
    defaultColumns,
  });

  const columnRegistry = useMemo(() => b2bOperationsErListColumns(resource), [resource]);
  const selectColumnsTestId = `${testId}_SelectColumnsButton`;

  const processingDateFilter = match(tab)
    .with('in_progress', () => ({
      from_processing_date: todayDateString(),
    }))
    .with('history', () => ({ to_processing_date: todayDateString() }))
    .exhaustive();

  return (
    <List
      actions={
        <TopToolbar>
          <B2bOperationsErActionsItem>
            <FilterButton data-testid="BoExternalB2bOperationsErListView_FilterButton" />
          </B2bOperationsErActionsItem>
          <B2bOperationsErActionsItem>
            <SelectColumnsButton
              preference="b2b_operations_er.list.columns"
              columns={columnRegistry}
              defaultColumns={defaultColumns}
              testId={selectColumnsTestId}
            />
          </B2bOperationsErActionsItem>
        </TopToolbar>
      }
      exporter={false}
      sort={defaultSort}
      filters={b2bOperationsErFilters}
      filter={processingDateFilter}
      disableSyncWithLocation={true}
      empty={false}
      sx={{
        '& .RaList-main': {
          width: '100%',
        },
      }}>
      <DatagridTyped<GetB2bOperationErOneResponse>
        bulkActionButtons={false}
        columnRegistry={columnRegistry}
        displayedColumns={displayedColumns}
        sortableColumns={getB2bOperationsErSortColumns}
      />
    </List>
  );
};
